import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Box,
  Typography,
} from "@mui/material";
import { KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { TableLoadingSkeleton } from "src/shared/components/tables/table-loading-skeleton";

export type ITreeNode = {
  id: string | number;
  code: string;
  name: string;
  node_type?: string;
  children?: ITreeNode[];
  opening_balance: {
    credit: number;
    debit: number;
  };
  movements: {
    credit: number;
    debit: number;
  };
};

type Props = {
  data: any[];
  isLoading: boolean;
};

const TrialBalanceTable: React.FC<Props> = ({ data, isLoading }) => {
  const { t: generalT } = useTranslation("general");

  const [expanded, setExpanded] = useState<Record<string | number, boolean>>(
    {},
  );

  const toggle = (id: string | number) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  let totalOpeningDebit = 0;
  let totalOpeningCredit = 0;
  let totalMovementDebit = 0;
  let totalMovementCredit = 0;

  const renderRows = (nodes: ITreeNode[], depth = 0): React.ReactNode[] => {
    return nodes.flatMap((node) => {
      const isOpen = expanded[node.id];
      const hasChildren = node.children && node.children?.length > 0;

      totalOpeningDebit += node.opening_balance?.debit ?? 0;
      totalOpeningCredit += node.opening_balance?.credit ?? 0;
      totalMovementDebit += node.movements?.debit ?? 0;
      totalMovementCredit += node.movements?.credit ?? 0;

      return [
        <TableRow
          key={node.id}
          hover
          sx={{
            bgcolor:
              node.node_type === "category" ? "primary.light" : "transparent",
          }}
        >
          <TableCell width={80}>{node.code}</TableCell>

          {/* only this column has hierarchy */}
          <TableCell size="small">
            <Box display="flex" alignItems="center" pl={depth * 3}>
              {hasChildren && (
                <IconButton size="small" onClick={() => toggle(node.id)}>
                  {isOpen ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                </IconButton>
              )}
              {!hasChildren && <Box width={32} />}
              {node.name}
            </Box>
          </TableCell>

          {/* these columns stay aligned */}
          <TableCell>{node.opening_balance?.debit.toFixed(2)}</TableCell>
          <TableCell>{node.opening_balance?.credit.toFixed(2)}</TableCell>
          <TableCell>{node.movements?.debit.toFixed(2)}</TableCell>
          <TableCell>{node.movements?.credit.toFixed(2)}</TableCell>
        </TableRow>,

        ...(hasChildren && isOpen ? renderRows(node.children!, depth + 1) : []),
      ];
    });
  };

  const tableHeaders = [
    "code",
    "accountName",
    "opening balance debit",
    "opening balance credit",
    "movement debit",
    "movement credit",
  ];

  return (
    <Table>
      <TableHead>
        <TableRow sx={{ bgcolor: "primary.main" }}>
          {tableHeaders.map((item) => (
            <TableCell key={item}>
              <Typography
                fontSize={"12px"}
                textTransform={"uppercase"}
                color={"#fff"}
                lineHeight={"18px"}
                fontWeight={500}
              >
                {generalT(item)}
              </Typography>
            </TableCell>
          ))}
        </TableRow>
      </TableHead>

      {isLoading ? (
        <TableLoadingSkeleton rowsLength={5} cellsLength={6} />
      ) : (
        <TableBody>
          {renderRows(data)}
          <TableRow>
            <TableCell sx={{ fontWeight: 600 }}>{generalT("total")}</TableCell>
            <TableCell></TableCell>
            <TableCell sx={{ fontWeight: 600 }}>
              {totalOpeningDebit.toFixed(2)}
            </TableCell>
            <TableCell sx={{ fontWeight: 600 }}>
              {totalOpeningCredit.toFixed(2)}
            </TableCell>
            <TableCell sx={{ fontWeight: 600 }}>
              {totalMovementDebit.toFixed(2)}
            </TableCell>
            <TableCell sx={{ fontWeight: 600 }}>
              {totalMovementCredit.toFixed(2)}
            </TableCell>
          </TableRow>
        </TableBody>
      )}
    </Table>
  );
};

export default TrialBalanceTable;
